// src/scenes/companies/CompanyTerminologies.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";
import { tokens } from "../../theme";
import Header from "../../components/Header";

const initialForm = { title: "", description: "" };

const CompanyTerminologies = () => {
  const { id: companyId } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Add dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [addConfirmOpen, setAddConfirmOpen] = useState(false);
  const [addForm, setAddForm] = useState(initialForm);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editConfirmOpen, setEditConfirmOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    id: null,
    title: "",
    description: "",
  });

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  /** ------------ FETCH ------------ */
  const fetchRows = () => {
    setLoading(true);
    axiosInstance
      .get(`/companies/${companyId}/terminologies`)
      .then((res) => setRows(res.data || []))
      .catch(() => toast.error("Failed to load terminologies"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  /** ------------ ADD ------------ */
  const openAdd = () => {
    setAddForm(initialForm);
    setAddOpen(true);
  };
  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setAddForm((p) => ({ ...p, [name]: value }));
  };
  const handleAddConfirm = () => {
    if (!addForm.title.trim()) return toast.warn("Title is required");
    setAddConfirmOpen(true);
  };
  const submitAdd = () => {
    setLoading(true);
    axiosInstance
      .post(`/companies/${companyId}/terminologies`, addForm)
      .then((res) => {
        toast.success("Terminology added");
        setRows((p) => [...p, res.data]);
        setAddOpen(false);
      })
      .catch(() => toast.error("Failed to add terminology"))
      .finally(() => {
        setLoading(false);
        setAddConfirmOpen(false);
      });
  };

  /** ------------ EDIT ------------ */
  const openEdit = (row) => {
    setEditForm({ id: row.id, title: row.title, description: row.description });
    setEditOpen(true);
  };
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((p) => ({ ...p, [name]: value }));
  };
  const handleEditConfirm = () => {
    if (!editForm.title.trim()) return toast.warn("Title is required");
    setEditConfirmOpen(true);
  };
  const submitEdit = () => {
    setLoading(true);
    axiosInstance
      .put(`/companies/${companyId}/terminologies/${editForm.id}`, {
        title: editForm.title,
        description: editForm.description,
      })
      .then((res) => {
        toast.success("Terminology updated");
        setRows((p) => p.map((r) => (r.id === editForm.id ? res.data : r)));
        setEditOpen(false);
      })
      .catch(() => toast.error("Failed to update terminology"))
      .finally(() => {
        setLoading(false);
        setEditConfirmOpen(false);
      });
  };

  /** ------------ DELETE ------------ */
  const openDelete = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };
  const submitDelete = () => {
    setLoading(true);
    axiosInstance
      .delete(`/companies/${companyId}/terminologies/${deleteId}`)
      .then(() => {
        toast.success("Terminology deleted");
        setRows((p) => p.filter((r) => r.id !== deleteId));
      })
      .catch(() => toast.error("Failed to delete terminology"))
      .finally(() => {
        setLoading(false);
        setDeleteOpen(false);
        setDeleteId(null);
      });
  };

  /** ------------ GRID ------------ */
  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 80 },
      { field: "title", headerName: "Title", flex: 1, minWidth: 180 },
      {
        field: "description",
        headerName: "Description",
        flex: 2,
        minWidth: 260,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box display="flex" gap={1}>
            <IconButton
              size="small"
              color="primary"
              disabled={loading}
              onClick={() => openEdit(row)}
              title="Edit"
            >
              {loading ? <CircularProgress size={18} /> : <EditIcon />}
            </IconButton>
            <IconButton
              size="small"
              color="error"
              disabled={loading}
              onClick={() => openDelete(row.id)}
              title="Delete"
            >
              {loading ? <CircularProgress size={18} /> : <DeleteIcon />}
            </IconButton>
          </Box>
        ),
      },
    ],
    [loading],
  );

  return (
    <Box m="20px">
      <Header
        title="COMPANY TERMINOLOGIES"
        subtitle={`Company ID: ${companyId}`}
      />

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
          variant="contained"
          sx={{
            backgroundColor: colors.greenAccent[500],
            color: colors.grey[900],
          }}
          disabled={loading}
          onClick={openAdd}
        >
          Add Terminology
        </Button>
      </Box>

      <Box
        height="70vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          loading={loading}
          pageSizeOptions={[5, 10, 20]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
          }}
        />
      </Box>

      {/* ADD dialog */}
      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Terminology</DialogTitle>
        <DialogContent sx={{ mt: 1, display: "grid", gap: 2 }}>
          <TextField
            label="Title"
            name="title"
            value={addForm.title}
            onChange={handleAddChange}
            fullWidth
            autoFocus
          />
          <TextField
            label="Description"
            name="description"
            value={addForm.description}
            onChange={handleAddChange}
            fullWidth
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            onClick={() => setAddOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddConfirm}
            disabled={loading}
            sx={{
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[900],
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ADD confirm */}
      <Dialog open={addConfirmOpen} onClose={() => setAddConfirmOpen(false)}>
        <DialogTitle>Save this terminology?</DialogTitle>
        <DialogActions>
          <Button
            color="error"
            onClick={() => setAddConfirmOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={submitAdd}
            disabled={loading}
            sx={{
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[900],
            }}
          >
            {loading ? <CircularProgress size={18} /> : "Yes, Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT dialog */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Terminology</DialogTitle>
        {editOpen && (
          <DialogContent sx={{ mt: 1, display: "grid", gap: 2 }}>
            <TextField
              label="Title"
              name="title"
              value={editForm.title}
              onChange={handleEditChange}
              fullWidth
              autoFocus
            />
            <TextField
              label="Description"
              name="description"
              value={editForm.description}
              onChange={handleEditChange}
              fullWidth
              multiline
              rows={4}
            />
          </DialogContent>
        )}
        <DialogActions>
          <Button
            color="error"
            onClick={() => setEditOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditConfirm}
            disabled={loading}
            sx={{
              backgroundColor: colors.blueAccent[500],
              color: colors.grey[900],
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT confirm */}
      <Dialog open={editConfirmOpen} onClose={() => setEditConfirmOpen(false)}>
        <DialogTitle>Save changes to this terminology?</DialogTitle>
        <DialogActions>
          <Button
            color="error"
            onClick={() => setEditConfirmOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={submitEdit}
            disabled={loading}
            sx={{
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[900],
            }}
          >
            {loading ? <CircularProgress size={18} /> : "Yes, Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE confirm */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete this terminology?</DialogTitle>
        <DialogActions>
          <Button
            color="error"
            onClick={() => setDeleteOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={submitDelete}
            disabled={loading}
            sx={{ color: colors.redAccent[500] }}
          >
            {loading ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyTerminologies;
